// File: src/pages/PaymentList.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Alert,
} from "reactstrap";
import { FaPlus, FaFilter } from "react-icons/fa";
import { ImDownload } from "react-icons/im";
import { Trash2, Edit } from "react-feather";
import swal from "sweetalert";

// ---- import from your shared layer ----
import {
  _Get,
  _Delete,
  _Put,
  CreateCustomerList,
} from "../import/ApiEndPoint/ApiCalling";
import {
  View_Receipt,
  Delete_payment_By_Id,
  View_Company_Details,
  Create_Account_List,
  View_Expense_Account,
  Create_Transporter_List,
  Update_payment_By_Id2,
} from "../import/ApiEndPoint/Api";
import { CheckPermission } from "../import/CheckPermission";
import SuperAdminUI from "../import/SuperAdminUI";

// ---- tiny CSS injected ONCE (page scoped) ----
const injectOnce = () => {
  if (document.getElementById("payment-inline-css")) return;
  const style = document.createElement("style");
  style.id = "payment-inline-css";
  style.innerHTML = `
    .pay-toolbar .count-pill{
      background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px;
    }
    .pay-table-wrap{ width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff; }
    .pay-table{ width:100%; border-collapse:collapse; table-layout:auto; min-width:720px; }
    .pay-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb; padding:6px 8px; position:sticky; top:0; z-index:1; text-align:center; white-space:nowrap;
    }
    .pay-table tbody td{
      padding:6px 8px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#0f172a; font-size:14px; text-align:center;
    }
    .pay-table tbody tr:hover{ background:#f9fbff; }
    .pay-table tfoot td{
      padding:8px 8px; border-top:2px solid #cbd5e1; background:#f8fafc; font-weight:800; text-align:center;
    }

    .action-chips{ display:flex; justify-content:center; align-items:center; gap:8px }
    .chip{ display:inline-grid; place-items:center; width:28px; height:28px; border-radius:16px; cursor:pointer }
    .chip-edit{ background:#d46f10; }
    .chip-delete{ background:#ec1809; }

    .shrink{ max-width: 220px; }
    .shrink--xs{ max-width: 140px; }
    .pay-editing-head{ background:#fff7ed !important; color:#9a3412 !important; }
    .pay-editing-cell{ background:#fffbeb; }
    .pay-editing-cell input, .pay-editing-cell select{ min-width:150px; height:32px; font-size:13px; }
  `;
  document.head.appendChild(style);
};

// ---- helpers ----
const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const norm = (s) => toStr(s).trim().toLowerCase();
const formatDate = (s) => (s ? String(s).split("T")[0] : "");

const CASH_ID = "CASH";
const LIST_PAGE_SIZE = 100;
const CASH_PARTY_SID = "CASH7845";

const getEntityId = (value) => {
  if (!value) return "";
  if (typeof value === "object") return String(value?._id || "");
  return String(value);
};

const getFinancialYearLabel = (dateValue = new Date()) => {
  const d =
    typeof dateValue === "string"
      ? new Date(`${dateValue}T00:00:00`)
      : new Date(dateValue);
  if (Number.isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  const startYear = month >= 4 ? year : year - 1;
  return `${startYear}-${String(startYear + 1).slice(-2)}`;
};

const uniqueById = (items = []) => {
  const seen = new Set();
  return items.filter((item) => {
    const id = String(item?._id || "");
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

const buildParticularOptions = (partyList = [], banks = []) => {
  const bankItems = uniqueById(banks).map((b) => ({
    ...b,
    bankAccount: true,
    isCash: String(b?._id || "") === CASH_ID || b?.isCash === true,
  }));

  return [...partyList, ...bankItems].map((it) => {
    if (it?.bankAccount) {
      const isCash = String(it?._id || "") === CASH_ID || it?.isCash === true;
      const name = it?.bankName || "Bank";
      const acc = it?.accountNumber ? ` (${it.accountNumber})` : "";
      return {
        value: `${isCash ? "cash" : "bank"}:${String(it?._id || "")}`,
        label: isCash ? "Cash" : `Bank: ${name}${acc}`,
        kind: isCash ? "cash" : "bank",
        id: String(it?._id || ""),
        raw: it,
      };
    }

    let kind = "expense";
    if (it?.party) kind = "party";
    else if (it?.User) kind = "user";
    else if (it?.transporter) kind = "transporter";

    return {
      value: `${kind}:${String(it?._id || "")}`,
      label: it?.fullName || "-",
      kind,
      id: String(it?._id || ""),
      raw: it,
    };
  });
};

const runInBatches = async (jobs, batchSize = 15) => {
  for (let i = 0; i < jobs.length; i += batchSize) {
    await Promise.all(jobs.slice(i, i + batchSize).map((job) => job()));
  }
};

// ✅ particular text helper
const getParticularText = (r) => {
  if (r?.expenseId?.title) return r.expenseId.title;
  if (r?.expenseId?.bankName) return r.expenseId.bankName;
  if (r?.partyId?.CompanyName) return r.partyId.CompanyName;
  if (r?.userId?.firstName) return r.userId.firstName;
  if (r?.transporterId?.companyName) return r.transporterId.companyName;
  return "-";
};

// ✅ payment bank resolvers
const getTxnBankId = (r) =>
  toStr(
    r?.bankDetails?._id || r?.bankDetails || r?.bankId || r?.bankDetailsId,
  ).trim();

const getTxnBankName = (r) =>
  toStr(
    r?.bankDetails?.bankName ||
      r?.bankName ||
      r?.expenseId?.bankName ||
      r?.expenseId?.title,
  ).trim();

const getTxnBankAccount = (r) =>
  toStr(
    r?.bankDetails?.accountNumber ||
      r?.accountNumber ||
      r?.bankAccountNumber ||
      r?.bankAccNo,
  ).trim();

const maskAcc = (acc) => {
  const s = toStr(acc).replace(/\s+/g, "");
  if (!s) return "";
  if (s.length <= 4) return s;
  return `XXXXXX${s.slice(-4)}`;
};

const bankLabel = (b) => {
  const bn = toStr(b?.bankName).trim() || "BANK";
  const br = toStr(b?.branchName).trim();
  const ac = toStr(b?.accountNumber).trim();
  const tail = maskAcc(ac);
  return `${bn}${tail ? ` (${tail})` : ""}${br ? ` - ${br}` : ""}`;
};

// ---- column storage key ----
const COL_KEY = "PaymentListCols";

export default function PaymentList() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [permissions, setPermissions] = useState({
    View: true,
    Create: true,
    Edit: true,
    Delete: true,
    Download: true,
    BulkUpload: false,
  });

  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  // Column-wise edit: one field at a time, no row/column selection checkboxes.
  const [editColumn, setEditColumn] = useState("");
  const [columnDrafts, setColumnDrafts] = useState({});
  const [savingColumn, setSavingColumn] = useState(false);
  const [particularOptions, setParticularOptions] = useState([]);
  const [particularLoading, setParticularLoading] = useState(false);
  const [particularLoadedFor, setParticularLoadedFor] = useState("");
  const [activeContext, setActiveContext] = useState({ uid: "", db: "" });

  // ✅ bank dropdown filter
  const [bankOptions, setBankOptions] = useState([]);
  const [bankFilter, setBankFilter] = useState("");

  // Column chooser state
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const addBufferRef = useRef([]);

  // MASTER switch
  const [isMaster, setIsMaster] = useState(false);

  useEffect(() => injectOnce(), []);

  // ----- Define base columns -----
  const baseColumns = useMemo(
    () => [
      { key: "date", header: "DATE", get: (r) => formatDate(r?.date) },
      {
        key: "particular",
        header: "PARTICULAR",
        get: (r) => getParticularText(r),
      },
      {
        key: "paymentMode",
        header: "PAYMENT MODE",
        get: (r) => r?.paymentMode || "-",
      },
      { key: "amount", header: "AMOUNT", get: (r) => r?.amount ?? "" },
      { key: "remark", header: "REMARK", get: (r) => r?.remark || "" },
    ],
    [],
  );

  const handleDeleteRef = useRef(() => {});

  const actionsColumn = useMemo(
    () => ({
      key: "actions",
      header: "ACTIONS",
      get: () => "",
      render: (r) => (
        <div className="action-chips">
          {permissions?.Edit && (
            <span
              className="chip chip-edit"
              title="Edit"
              onClick={() => navigate(`/app/payments/create/${r?._id}`)}
            >
              <Edit size={14} color="white" />
            </span>
          )}
          {permissions?.Delete && (
            <span
              className="chip chip-delete"
              title="Delete"
              onClick={() => handleDeleteRef.current(r?._id)}
            >
              <Trash2 size={14} color="white" />
            </span>
          )}
        </div>
      ),
    }),
    [permissions, navigate],
  );

  // column map for chooser
  const colMap = useMemo(() => {
    const map = new Map();
    baseColumns.forEach((c) => map.set(c.key, c));
    map.set(actionsColumn.key, actionsColumn);
    return map;
  }, [baseColumns, actionsColumn]);

  // Restore or default columns
  const [selectedKeys, setSelectedKeys] = useState([]);
  useEffect(() => {
    const raw = localStorage.getItem(COL_KEY);
    let nextKeys = [];
    try {
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length) {
        nextKeys = parsed.filter((k) => colMap.has(k));
      }
    } catch {}
    if (!nextKeys.length) nextKeys = baseColumns.map((c) => c.key);
    setSelectedKeys(nextKeys);
  }, [baseColumns, colMap]);

  // ✅ keep bankFilter valid after company reload
  useEffect(() => {
    if (!bankOptions?.length) {
      setBankFilter("");
      return;
    }

    const ok = bankOptions.some((b) => toStr(b?._id) === bankFilter);

    if (!bankFilter || !ok) {
      setBankFilter(toStr(bankOptions[0]?._id));
    }
  }, [bankOptions, bankFilter]);

  // ----- Load company banks -----
  const loadCompanyBanks = async (uid, db) => {
    try {
      const url = `${View_Company_Details}${encodeURIComponent(uid)}/`;
      const res = await _Get(url, db);

      const banks = Array.isArray(res?.CompanyDetail?.bankDetails)
        ? res.CompanyDetail.bankDetails
        : [];

      const cashOption = {
        _id: CASH_ID,
        bankName: "Cash",
        accountNumber: "",
        isCash: true,
      };

      const bankList = [cashOption, ...banks];
      setBankOptions(bankList);

      const selectedBankName = toStr(res?.CompanyDetail?.selectedBank).trim();

      if (bankList.length) {
        const match = selectedBankName
          ? bankList.find((b) => norm(b?.bankName) === norm(selectedBankName))
          : null;

        setBankFilter(toStr(match?._id || banks[0]?._id || CASH_ID));
      } else {
        setBankFilter("");
      }
    } catch {
      setBankOptions([
        { _id: CASH_ID, bankName: "Cash", accountNumber: "", isCash: true },
      ]);
      setBankFilter(CASH_ID);
    }
  };

  // ----- Fetch data -----
  const readFor = async (uid, db) => {
    const financialYearSelected = localStorage.getItem("financialYearSelected");
    const selectedDatabase = financialYearSelected
      ? `${db}-${financialYearSelected}`
      : db;

    const res = await _Get(View_Receipt, selectedDatabase);
    const list = Array.isArray(res?.Receipts) ? res.Receipts : [];

    const payments = list.filter((x) =>
      String(x?.type || "")
        .toLowerCase()
        .includes("payment"),
    );

    payments.sort((a, b) => new Date(b?.date || 0) - new Date(a?.date || 0));

    setRows(payments);
  };

  // ✅ combined loader
  const loadFor = async (uid, db) => {
    setActiveContext({ uid, db });
    setParticularLoadedFor("");
    setParticularOptions([]);
    // These calls are independent, so do them together instead of waiting for
    // company details before starting the transaction request.
    await Promise.all([loadCompanyBanks(uid, db), readFor(uid, db)]);
  };

  const reload = async () => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}");
    const uid = user?._id || user?.id || "";
    const db = user?.database || user?.db || "";

    const inside = CheckPermission("Payment");
    if (inside && typeof inside === "object") {
      setPermissions((prev) => ({ ...prev, ...inside }));
    }

    setIsMaster(user?.rolename?.roleName === "MASTER");

    await loadFor(uid, db);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErrorMsg("");
      try {
        await reload();
      } catch (e) {
        console.error("[PaymentList] fetch error:", e);
        setErrorMsg(
          e?.response?.data?.message ||
            e?.message ||
            "Unable to load payments. Check console for details.",
        );
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----- Visible columns & filters -----
  const visibleCols = useMemo(() => {
    const cols = selectedKeys.map((k) => colMap.get(k)).filter(Boolean);
    return [...cols, actionsColumn];
  }, [selectedKeys, colMap, actionsColumn]);

  const tableCols = visibleCols;

  const filteredRows = useMemo(() => {
    let base = Array.isArray(rows) ? rows : [];

    // ✅ bank/cash filter
    // If bankFilter is selected, show only that bank/cash payment.
    if (bankFilter) {
      const sel = (bankOptions || []).find((b) => toStr(b?._id) === bankFilter);
      const isCashFilter = norm(bankFilter) === norm(CASH_ID);

      base = base.filter((r) => {
        if (isCashFilter) {
          const rid = getTxnBankId(r);
          const rName = getTxnBankName(r);
          const mode = toStr(r?.paymentMode);

          return (
            norm(rid) === norm(CASH_ID) ||
            norm(rName) === "cash" ||
            norm(mode) === "cash"
          );
        }

        const rid = getTxnBankId(r);
        if (rid && norm(rid) === norm(bankFilter)) return true;

        const rName = getTxnBankName(r);
        if (sel?.bankName && norm(rName) === norm(sel.bankName)) return true;

        const rAcc = getTxnBankAccount(r);
        const selAcc = toStr(sel?.accountNumber).trim();
        if (selAcc && rAcc && toStr(rAcc) === selAcc) return true;

        return false;
      });
    }

    // ✅ search
    const term = search.trim().toLowerCase();
    if (!term) return base;

    const searchCols = visibleCols.filter((c) => c.key !== "actions");
    return base.filter((r) =>
      searchCols.some((c) => toStr(c.get(r)).toLowerCase().includes(term)),
    );
  }, [rows, bankFilter, bankOptions, search, visibleCols]);

  // ✅ total amount based on selected bank/cash and search
  const totalAmount = useMemo(() => {
    return (filteredRows || []).reduce(
      (sum, r) => sum + (Number(r?.amount) || 0),
      0,
    );
  }, [filteredRows]);

  const effectivePageSize = editColumn === "particular" ? 25 : LIST_PAGE_SIZE;

  const totalPages = Math.max(
    1,
    Math.ceil(filteredRows.length / effectivePageSize),
  );

  const pageRows = useMemo(() => {
    const start = (page - 1) * effectivePageSize;
    return filteredRows.slice(start, start + effectivePageSize);
  }, [filteredRows, page, effectivePageSize]);

  useEffect(() => {
    setPage(1);
  }, [bankFilter, search]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  // ----- Actions -----
  const handleDelete = useCallback((id) => {
    swal("Warning", "Sure you want to delete it?", {
      buttons: { cancel: "Cancel", catch: { text: "Delete", value: "delete" } },
    }).then((value) => {
      if (value === "delete") {
        _Delete(Delete_payment_By_Id, id)
          .then(() => {
            setRows((prev) => prev.filter((x) => x._id !== id));
          })
          .catch(() => {});
      }
    });
  }, []);

  useEffect(() => {
    handleDeleteRef.current = handleDelete;
  }, [handleDelete]);

  const handleExportXLSX = async (fileName) => {
    const [{ default: XLSXDefault, ...XLSXNamed }, fileSaver] =
      await Promise.all([import("xlsx"), import("file-saver")]);
    const XLSX = XLSXDefault || XLSXNamed;
    const saveAs = fileSaver.saveAs || fileSaver.default;

    // Export every list column with readable/full column names,
    // regardless of which columns are currently visible on screen.
    const cols = baseColumns;
    const headerRow = cols.map((c) => c.header);
    const dataRows = filteredRows.map((r) => cols.map((c) => toStr(c.get(r))));

    const ws = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);
    ws["!cols"] = cols.map((c) => ({
      wch: Math.max(14, Math.min(40, c.header.length + 8)),
    }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Payment List");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      `${fileName}.xlsx`,
    );
  };

  // ----- Column-wise editing -----
  const ensureParticularOptions = useCallback(async () => {
    const uid = activeContext?.uid;
    const db = activeContext?.db;
    if (!uid || !db) return;

    const contextKey = `${uid}|${db}`;
    if (particularLoadedFor === contextKey && particularOptions.length) return;

    setParticularLoading(true);
    try {
      const accountsURL = `${Create_Account_List}${uid}/`;
      const [customerRes, accountRes, expenseRes, transporterRes] =
        await Promise.allSettled([
          CreateCustomerList(uid, db),
          _Get(accountsURL, db),
          _Get(View_Expense_Account, db),
          _Get(Create_Transporter_List, db),
        ]);

      const customers = Array.isArray(customerRes?.value?.Customer)
        ? customerRes.value.Customer.map((c) => ({
            ...c,
            party: true,
            fullName:
              (c?.CompanyName && c.CompanyName.trim()) ||
              `${c?.firstName || ""} ${c?.lastName || ""}`.trim() ||
              c?.mobileNumber ||
              "Customer",
          }))
        : [];

      const users = Array.isArray(accountRes?.value?.adminDetails)
        ? accountRes.value.adminDetails.map((u) => ({
            ...u,
            User: true,
            fullName:
              `${u?.firstName || ""} ${u?.lastName || ""}`.trim() ||
              u?.email ||
              "User",
          }))
        : [];

      const expenses = Array.isArray(expenseRes?.value?.Expenses)
        ? expenseRes.value.Expenses.map((e) => ({
            ...e,
            miscellaneous: true,
            fullName:
              [e?.title, e?.type].filter(Boolean).join(" ").trim() || "Expense",
          }))
        : [];

      const transporters = Array.isArray(transporterRes?.value?.Transporter)
        ? transporterRes.value.Transporter.map((tr) => ({
            ...tr,
            transporter: true,
            fullName: tr?.companyName || "-",
          }))
        : [];

      setParticularOptions(
        buildParticularOptions(
          [...customers, ...users, ...expenses, ...transporters],
          bankOptions,
        ),
      );
      setParticularLoadedFor(contextKey);
    } catch (error) {
      console.error("[PaymentList] particular options error:", error);
      swal("Unable to load Particular dropdown options");
    } finally {
      setParticularLoading(false);
    }
  }, [
    activeContext,
    bankOptions,
    particularLoadedFor,
    particularOptions.length,
  ]);

  const getRowParticularValue = useCallback(
    (row) => {
      const partyId = getEntityId(row?.partyId);
      if (partyId) return `party:${partyId}`;

      const userId = getEntityId(row?.userId);
      if (userId) return `user:${userId}`;

      const transporterId = getEntityId(row?.transporterId);
      if (transporterId) return `transporter:${transporterId}`;

      const expenseId = getEntityId(row?.expenseId);
      if (!expenseId) return "";
      if (String(expenseId) === CASH_ID) return `cash:${CASH_ID}`;
      if (bankOptions.some((b) => String(b?._id || "") === String(expenseId))) {
        return `bank:${expenseId}`;
      }
      return `expense:${expenseId}`;
    },
    [bankOptions],
  );

  const getEditValue = useCallback(
    (row, key) => {
      const rowId = toStr(row?._id);
      if (Object.prototype.hasOwnProperty.call(columnDrafts, rowId)) {
        return columnDrafts[rowId];
      }

      if (key === "date") return formatDate(row?.date);
      if (key === "particular") return getRowParticularValue(row);
      if (key === "paymentMode") {
        const bankId = getTxnBankId(row);
        return String(bankId).toUpperCase() === CASH_ID
          ? "Cash"
          : row?.paymentMode || "Bank";
      }
      if (key === "amount") return row?.amount ?? "";
      if (key === "remark") return row?.remark || "";
      return "";
    },
    [columnDrafts, getRowParticularValue],
  );

  const setDraftValue = useCallback((rowId, value) => {
    const id = toStr(rowId);
    if (!id) return;
    setColumnDrafts((prev) => ({ ...prev, [id]: value }));
  }, []);

  const handleEditColumnChange = async (e) => {
    const key = e.target.value;
    setEditColumn(key);
    setColumnDrafts({});
    setPage(1);

    if (key && !selectedKeys.includes(key)) {
      setSelectedKeys((prev) => [...prev, key]);
    }

    if (key === "particular") {
      await ensureParticularOptions();
    }
  };

  const cancelColumnEdit = () => {
    setEditColumn("");
    setColumnDrafts({});
    setSavingColumn(false);
  };

  const buildColumnUpdatePayload = (row, key, value) => {
    const financialYearSelected = localStorage.getItem("financialYearSelected");
    const selectedDatabase = financialYearSelected
      ? `${activeContext?.db}-${financialYearSelected}`
      : activeContext?.db;
    const payload = { database: selectedDatabase };

    if (key === "date") {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ""))) {
        throw new Error("Please enter a valid date");
      }
      payload.date = value;
      payload.financialYear = getFinancialYearLabel(value);
    } else if (key === "particular") {
      const option = particularOptions.find((o) => o.value === value);
      if (!option) throw new Error("Please select a valid Particular");

      payload.partyId = null;
      payload.userId = null;
      payload.transporterId = null;
      payload.expenseId = null;

      if (option.kind === "party") payload.partyId = option.id;
      else if (option.kind === "user") payload.userId = option.id;
      else if (option.kind === "transporter") payload.transporterId = option.id;
      else if (option.kind === "expense") payload.expenseId = option.id;
      else if (option.kind === "bank") payload.expenseId = option.id;
      else if (option.kind === "cash") payload.expenseId = CASH_ID;

      const paymentMode = String(row?.paymentMode || "").toLowerCase();
      const isCashParty =
        option.kind === "party" &&
        String(option?.raw?.sId || "")
          .trim()
          .toUpperCase() === CASH_PARTY_SID;
      payload.isCashToBank = false;
      payload.isBankToCash = isCashParty && paymentMode === "bank";
    } else if (key === "paymentMode") {
      const mode = String(value || "Bank") === "Cash" ? "Cash" : "Bank";
      payload.paymentMode = mode;

      if (mode === "Cash") {
        payload.bankDetails = CASH_ID;
        payload.bankSelect = "Cash";
      } else {
        const currentBankId = getTxnBankId(row);
        const currentIsRealBank =
          currentBankId && String(currentBankId).toUpperCase() !== CASH_ID;
        const selectedBank = currentIsRealBank
          ? bankOptions.find(
              (b) => String(b?._id || "") === String(currentBankId),
            )
          : bankOptions.find((b) => String(b?._id || "") !== CASH_ID);
        if (!selectedBank)
          throw new Error("No bank account is available for Bank mode");
        payload.bankDetails = selectedBank?._id || null;
        payload.bankSelect = selectedBank?.bankName || "";
      }

      const party = typeof row?.partyId === "object" ? row.partyId : null;
      payload.isCashToBank = false;
      payload.isBankToCash =
        String(party?.sId || "")
          .trim()
          .toUpperCase() === CASH_PARTY_SID && mode.toLowerCase() === "bank";
    } else if (key === "amount") {
      const amount = Number(value);
      if (!Number.isFinite(amount) || amount <= 0) {
        throw new Error("Amount must be greater than 0");
      }
      payload.amount = amount;
      payload.preAmount = amount;
    } else if (key === "remark") {
      payload.remark = String(value ?? "");
    }

    return payload;
  };

  const saveColumnChanges = async () => {
    const entries = Object.entries(columnDrafts);
    if (!editColumn) return;
    if (!entries.length) {
      swal("No changes made in this column");
      return;
    }

    const rowMap = new Map((rows || []).map((r) => [toStr(r?._id), r]));

    try {
      setSavingColumn(true);
      const jobs = entries.map(([rowId, value]) => () => {
        const row = rowMap.get(rowId);
        if (!row) return Promise.resolve();
        const payload = buildColumnUpdatePayload(row, editColumn, value);
        return _Put(Update_payment_By_Id2, rowId, payload);
      });

      await runInBatches(jobs);
      const savedCount = entries.length;
      cancelColumnEdit();
      await readFor(activeContext?.uid, activeContext?.db);
      swal(`${savedCount} payment(s) updated successfully`);
    } catch (error) {
      console.error("[PaymentList] column save error:", error);
      swal(
        "Error",
        error?.message || "Could not update the selected column",
        "error",
      );
      setSavingColumn(false);
    }
  };

  const renderColumnCell = (row, column) => {
    if (!editColumn || column.key !== editColumn) {
      return typeof column.render === "function"
        ? column.render(row)
        : toStr(column.get(row));
    }

    const rowId = toStr(row?._id);
    const value = getEditValue(row, column.key);

    if (column.key === "particular") {
      return (
        <Input
          type="select"
          value={value || ""}
          disabled={particularLoading}
          onChange={(e) => setDraftValue(rowId, e.target.value)}
        >
          <option value="">
            {particularLoading ? "Loading Particulars..." : "--Select--"}
          </option>
          {value &&
            !particularOptions.some((option) => option.value === value) && (
              <option value={value}>{getParticularText(row)}</option>
            )}
          {particularOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Input>
      );
    }

    if (column.key === "paymentMode") {
      return (
        <Input
          type="select"
          value={value || "Bank"}
          onChange={(e) => setDraftValue(rowId, e.target.value)}
        >
          <option value="Bank">Bank</option>
          <option value="Cash">Cash</option>
        </Input>
      );
    }

    if (column.key === "date") {
      return (
        <Input
          type="date"
          value={value || ""}
          onChange={(e) => setDraftValue(rowId, e.target.value)}
        />
      );
    }

    if (column.key === "amount") {
      return (
        <Input
          type="number"
          min="0"
          step="0.01"
          value={value}
          onChange={(e) => setDraftValue(rowId, e.target.value)}
        />
      );
    }

    return (
      <Input
        type="text"
        value={value}
        onChange={(e) => setDraftValue(rowId, e.target.value)}
      />
    );
  };

  // ----- Column Chooser Modal logic -----
  const openModal = () => {
    addBufferRef.current = [];
    setSelectedIndex(-1);
    setModal(true);
  };
  const closeModal = () => setModal(false);

  const handleCheckbox = (checked, colKey) => {
    const buf = addBufferRef.current.slice();
    if (checked) {
      if (!buf.includes(colKey)) buf.push(colKey);
    } else {
      const i = buf.indexOf(colKey);
      if (i >= 0) buf.splice(i, 1);
    }
    addBufferRef.current = buf;
  };

  const addSelectedColumns = () => {
    const next = Array.from(
      new Set([...selectedKeys, ...addBufferRef.current]),
    );
    setSelectedKeys(next);
  };

  const removeAtIndex = () => {
    if (selectedIndex < 0) return;
    const next = selectedKeys.slice();
    next.splice(selectedIndex, 1);
    setSelectedKeys(next);
    setSelectedIndex(-1);
  };

  const shiftUp = () => {
    if (selectedIndex <= 0) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex - 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex - 1);
  };

  const shiftDown = () => {
    if (selectedIndex < 0 || selectedIndex >= selectedKeys.length - 1) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex + 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex + 1);
  };

  const applyColumns = (e) => {
    e.preventDefault();
    localStorage.setItem(COL_KEY, JSON.stringify(selectedKeys));
    closeModal();
  };

  // ----- Master selector handlers -----
  const handleParentSubmit = async (e) => {
    e.preventDefault();
    const picked = JSON.parse(
      localStorage.getItem("SuperadminIdByMaster") || "null",
    );
    try {
      setLoading(true);
      if (picked) {
        const [id, db] = String(picked).split(" ");
        await loadFor(id, db);
      } else {
        await reload();
      }
    } finally {
      setLoading(false);
    }
  };

  const noData = !filteredRows?.length;

  // ----- Loading -----
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  return (
    <>
      {/* Error banner if fetch failed */}
      {errorMsg && (
        <Row>
          <Col sm="12">
            <Alert color="danger" style={{ marginBottom: 10 }}>
              {errorMsg}
            </Alert>
          </Col>
        </Row>
      )}

      {/* === Toolbar === */}
      <Row className="pay-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            style={{
              paddingTop: 4,
              paddingBottom: 4,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
              background: "#f4f7f6",
            }}
          >
            <CardBody style={{ padding: 10 }}>
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    fontWeight: 800,
                    textTransform: "uppercase",
                    fontSize: 18,
                  }}
                >
                  Payment List{" "}
                  <span className="count-pill" title="records">
                    {filteredRows.length}
                  </span>
                </div>

                {/* MASTER SuperAdmin switcher */}
                <div style={{ display: "flex", gap: 10 }}>
                  {isMaster && (
                    <div style={{ minWidth: 260 }}>
                      <SuperAdminUI
                        onDropdownChange={(val) =>
                          localStorage.setItem(
                            "SuperadminIdByMaster",
                            JSON.stringify(val),
                          )
                        }
                        onSubmit={handleParentSubmit}
                      />
                    </div>
                  )}
                </div>

                <div style={{ flex: "1 1 160px" }} />

                {/* ✅ Bank/Cash dropdown filter */}
                <div style={{ minWidth: 320 }}>
                  <Input
                    type="select"
                    value={bankFilter}
                    onChange={(e) => setBankFilter(e.target.value)}
                    style={{ background: "#fff" }}
                    title="Filter by bank account"
                    disabled={!bankOptions.length}
                  >
                    {!bankOptions.length && (
                      <option value="">No Bank Accounts</option>
                    )}

                    {(bankOptions || []).map((b) => (
                      <option key={toStr(b?._id)} value={toStr(b?._id)}>
                        {String(b?._id || "") === CASH_ID
                          ? "Cash"
                          : bankLabel(b)}
                      </option>
                    ))}
                  </Input>
                </div>

                {/* Compact Search */}
                <div
                  style={{
                    minWidth: 220,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <Input
                    placeholder="Search payments..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ background: "#fff" }}
                    className="shrink"
                  />
                </div>

                {/* Create */}
                {permissions?.Create && (
                  <Button
                    style={{
                      backgroundColor: "rgb(8, 91, 245)",
                      color: "white",
                      fontWeight: 600,
                      height: 32,
                      fontSize: 14,
                      textTransform: "uppercase",
                    }}
                    onClick={() => navigate("/app/payments/create/0")}
                  >
                    <FaPlus size={10} /> Payment
                  </Button>
                )}

                {permissions?.Edit && (
                  <div style={{ minWidth: 170 }}>
                    <Input
                      type="select"
                      value={editColumn}
                      onChange={handleEditColumnChange}
                      style={{
                        height: 32,
                        background: "#fff",
                        fontWeight: 700,
                      }}
                      title="Choose one column to edit"
                    >
                      <option value="">Edit Column</option>
                      {baseColumns.map((column) => (
                        <option key={column.key} value={column.key}>
                          {column.header}
                        </option>
                      ))}
                    </Input>
                  </div>
                )}

                {editColumn && (
                  <>
                    <Button
                      color="success"
                      onClick={saveColumnChanges}
                      disabled={
                        savingColumn ||
                        particularLoading ||
                        !Object.keys(columnDrafts).length
                      }
                      style={{
                        height: 32,
                        fontWeight: 700,
                        textTransform: "uppercase",
                      }}
                    >
                      {savingColumn
                        ? "Saving..."
                        : `Save ${baseColumns.find((c) => c.key === editColumn)?.header || "Column"} (${Object.keys(columnDrafts).length})`}
                    </Button>
                    <Button
                      color="secondary"
                      onClick={cancelColumnEdit}
                      disabled={savingColumn}
                      style={{
                        height: 32,
                        fontWeight: 700,
                        textTransform: "uppercase",
                      }}
                    >
                      Cancel
                    </Button>
                  </>
                )}

                {/* Column chooser */}
                <Button
                  outline
                  color="primary"
                  onClick={openModal}
                  title="Filter Columns"
                  style={{
                    height: 32,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaFilter size={14} />
                </Button>

                {/* Excel download only */}
                {permissions?.Download && (
                  <ImDownload
                    onClick={() => handleExportXLSX("PaymentList")}
                    style={{ cursor: "pointer" }}
                    title="Download Excel"
                    size={24}
                    color="rgb(8, 91, 245)"
                  />
                )}
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* === Table === */}
      <Row>
        <Col sm="12">
          <Card
            style={{
              padding: 8,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
            }}
          >
            {editColumn && (
              <div
                style={{
                  marginBottom: 8,
                  padding: "8px 10px",
                  borderRadius: 10,
                  background: "#fff7ed",
                  border: "1px solid #fed7aa",
                  color: "#9a3412",
                  fontWeight: 600,
                  fontSize: 13,
                }}
              >
                Editing only the{" "}
                <b>{baseColumns.find((c) => c.key === editColumn)?.header}</b>{" "}
                column. Change any rows you need, then click Save. Other columns
                stay read-only.
              </div>
            )}
            {noData ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                No payments found.
              </div>
            ) : (
              <div className="pay-table-wrap">
                <table className="pay-table">
                  <thead>
                    <tr>
                      {tableCols.map((column) => (
                        <th
                          key={column.key}
                          className={
                            editColumn === column.key ? "pay-editing-head" : ""
                          }
                        >
                          {column.header}
                        </th>
                      ))}
                    </tr>
                  </thead>

                  <tbody>
                    {pageRows.map((row) => (
                      <tr key={row?._id}>
                        {tableCols.map((column) => (
                          <td
                            key={column.key}
                            className={
                              editColumn === column.key
                                ? "pay-editing-cell"
                                : ""
                            }
                          >
                            {renderColumnCell(row, column)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>

                  <tfoot>
                    <tr>
                      {tableCols.map((column, index) => {
                        if (column.key === "amount") {
                          return (
                            <td key={column.key}>
                              {Number(totalAmount || 0).toFixed(2)}
                            </td>
                          );
                        }
                        if (index === 0) return <td key={column.key}>TOTAL</td>;
                        return <td key={column.key}></td>;
                      })}
                    </tr>
                  </tfoot>
                </table>
                {filteredRows.length > effectivePageSize && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                      gap: 8,
                      padding: "10px 4px 2px",
                    }}
                  >
                    <Button
                      size="sm"
                      outline
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      Previous
                    </Button>
                    <span style={{ fontSize: 13, fontWeight: 700 }}>
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      size="sm"
                      outline
                      disabled={page >= totalPages}
                      onClick={() =>
                        setPage((p) => Math.min(totalPages, p + 1))
                      }
                    >
                      Next
                    </Button>
                  </div>
                )}
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Column Chooser Modal */}
      <Modal
        isOpen={modal}
        toggle={closeModal}
        zIndex={2000}
        backdrop="static"
        keyboard={false}
        style={{ maxWidth: 900 }}
      >
        <ModalHeader toggle={closeModal}>Change Fields</ModalHeader>
        <ModalBody>
          <Row>
            <Col lg="4" md="4" sm="12" xl="4" xs="12">
              <h5>Available Columns</h5>
              <div style={listBoxStyle}>
                {baseColumns.map((col) => (
                  <div key={col.key} style={pickItemStyle}>
                    <label
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        type="checkbox"
                        onChange={(e) =>
                          handleCheckbox(e.target.checked, col.key)
                        }
                      />
                      <span>{col.header}</span>
                    </label>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8 }}>
                <Button size="sm" color="primary" onClick={addSelectedColumns}>
                  Add Selected →
                </Button>
              </div>
            </Col>

            <Col lg="8" md="8" sm="12" xl="8" xs="12">
              <h5>Visible Columns</h5>
              <div
                style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
              >
                <div style={{ ...listBoxStyle, flex: 1 }}>
                  {selectedKeys.map((key, i) => {
                    const col = colMap.get(key);
                    if (!col) return null;
                    return (
                      <div
                        key={key}
                        style={{
                          ...pickItemStyle,
                          background: i === selectedIndex ? "#1877f2" : "#fff",
                          color: i === selectedIndex ? "#fff" : "#000",
                          cursor: "pointer",
                        }}
                        onClick={() => setSelectedIndex(i)}
                      >
                        {col.header}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  <Button size="sm" onClick={shiftUp}>
                    ↑
                  </Button>
                  <Button size="sm" onClick={shiftDown}>
                    ↓
                  </Button>
                  <Button size="sm" color="danger" onClick={removeAtIndex}>
                    Remove
                  </Button>
                </div>
              </div>
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <Button color="primary" onClick={applyColumns}>
                  Submit
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>
    </>
  );
}

/* little styles */

const listBoxStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  maxHeight: 280,
  overflow: "auto",
  background: "#fff",
  padding: 8,
};

const pickItemStyle = {
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid #e5e7eb",
  marginBottom: 6,
  background: "#fff",
};
